import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import importService from '../../services/importService';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDate } from '../../utils/formatters';

const ImportDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [importData, setImportData] = useState(null);
  const [changeLogs, setChangeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchImportDetails = async () => {
      try {
        setLoading(true);
        
        const result = await importService.getImportDetails(id);
        
        if (result.success) {
          setImportData(result.data.import);
          setChangeLogs(result.data.changes || []);
        } else {
          setError('Erreur lors du chargement des détails de l\'importation.');
        }
      } catch (error) {
        console.error('Erreur lors du chargement des détails:', error);
        setError('Erreur lors du chargement des détails de l\'importation.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchImportDetails();
  }, [id]);
  
  if (loading) {
    return <LoadingSpinner message="Chargement des détails de l'importation..." />;
  }
  
  if (error) {
    return (
      <div className="bg-red-100 p-4 rounded-md text-red-700">
        {error}
      </div>
    );
  }
  
  if (!importData) {
    return (
      <div className="bg-yellow-100 p-4 rounded-md text-yellow-700">
        Détails de l'importation non trouvés.
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Détails de l'importation</h2>
        <button
          onClick={() => navigate('/imports')}
          className="btn btn-secondary"
        >
          Retour
        </button>
      </div>
      
      {/* Informations générales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium mb-2">Informations générales</h3>
          <div className="space-y-2">
            <p><span className="font-medium">Fichier:</span> {importData.filename}</p>
            <p><span className="font-medium">Date:</span> {formatDate(importData.startTime, true)}</p>
            <p><span className="font-medium">Durée:</span> {importData.endTime ? formatDuration(importData.startTime, importData.endTime) : 'N/A'}</p>
            <p><span className="font-medium">Importé par:</span> {importData.importedBy?.username || 'Système'}</p>
            <p><span className="font-medium">Type:</span> {importData.importType === 'manual' ? 'Manuel' : 'Programmé'}</p>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium mb-2">Statistiques</h3>
          <div className="space-y-2">
            <p><span className="font-medium">Total d'enregistrements:</span> {importData.recordsCount}</p>
            <p><span className="font-medium">Nouveaux:</span> {importData.newRecords}</p>
            <p><span className="font-medium">Mis à jour:</span> {importData.updatedRecords}</p>
            <p><span className="font-medium">Invalides:</span> {importData.invalidRecords || 0}</p>
            <p><span className="font-medium">Statut:</span> 
              <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(importData.status)}`}>
                {
                  importData.status === 'success' ? 'Succès' :
                  importData.status === 'failed' ? 'Échec' :
                  importData.status === 'in-progress' ? 'En cours' :
                  'En attente'
                }
              </span>
            </p>
          </div>
        </div>
      </div>
      
      {/* Logs d'erreur */}
      {importData.errorLog && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Erreurs</h3>
          <div className="bg-red-50 p-4 rounded-lg overflow-auto max-h-64 text-sm">
            <pre className="whitespace-pre-wrap">{importData.errorLog}</pre>
          </div>
        </div>
      )}
      
      {/* Logs de modifications */}
      {changeLogs.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-2">Modifications détectées ({changeLogs.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contrat
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Champ
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ancienne valeur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nouvelle valeur
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {changeLogs.flatMap((log) => 
                  log.changes.map((change, index) => (
                    <tr key={`${log.contractNumber}-${change.field}-${index}`}>
                      {index === 0 ? (
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900" rowSpan={log.changes.length}>
                          {log.contractNumber}
                        </td>
                      ) : null}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {change.label}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatChangeValue(change.oldValue)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatChangeValue(change.newValue)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Fonction pour formater la durée
const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 'N/A';
  
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  // Calculer la différence en millisecondes
  const diff = end.getTime() - start.getTime();
  
  // Calculer les secondes
  const seconds = Math.floor(diff / 1000);
  
  if (seconds < 60) {
    return `${seconds} s`;
  } else {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} min ${remainingSeconds} s`;
  }
};

// Obtenir la classe de statut
const getStatusClass = (status) => {
  switch (status) {
    case 'success':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'in-progress':
      return 'bg-yellow-100 text-yellow-800';
    case 'pending':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Formater les valeurs des changements
const formatChangeValue = (value) => {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">Non défini</span>;
  }
  
  if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
    return formatDate(value);
  }
  
  return String(value);
};

export default ImportDetails;